import { GetStaticPaths, GetStaticProps } from "next";
import { range, toNumber } from "lodash";
import { FrontMatter, loadPostFrontMatter, postSlugs } from "../../../lib/blog";
import Image from "next/future/image";
import Link from "next/link";

const PAGE_SIZE = 5;

const calculatePagesCount = (totalCount: number) => {
  // we suppose that if we have 0 items we want 1 empty page
  return totalCount < PAGE_SIZE ? 1 : Math.ceil(totalCount / PAGE_SIZE);
};

interface Props {
  posts: Omit<FrontMatter, "date">[];
  totalCount: number;
  current: number;
}

export const getStaticProps: GetStaticProps<Props> = async (context) => {
  const page = toNumber(context?.params?.page);

  const frontMatters = postSlugs
    .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    .map((slug) => loadPostFrontMatter(slug));

  return {
    props: {
      // trunk-ignore(eslint/@typescript-eslint/no-unused-vars)
      posts: frontMatters.map(({ date, ...props }) => ({
        ...props,
      })),
      totalCount: calculatePagesCount(postSlugs.length),
      current: page,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: range(1, calculatePagesCount(postSlugs.length) + 1).map((page) => ({
      params: { page: page.toString() },
    })),
    fallback: false,
  };
};

export default function Index({ posts, current, totalCount }: Props) {
  return (
    <>
      <div className="bg-[#f5f6fd] w-full pt-24 pb-8 flex-grow">
        <div className="max-w-6xl mx-auto bg-white border xl:rounded-xl xl:shadow-sm p-2 sm:p-8 flex flex-col gap-8">
          {posts.map((post) => (
            <Link
              key={post.title}
              className="group flex flex-col sm:flex-row gap-6"
              href={{
                pathname: "/blog/[slug]",
                query: {
                  slug: post.slug,
                },
              }}
            >
              <Image
                src={post.cover}
                alt="Photo for post"
                width={500}
                height={500}
                sizes="30vw"
                className="rounded-lg w-full sm:w-48 h-auto object-cover"
              ></Image>
              <div className="flex flex-col gap-y-2">
                <div className="text-xl font-bold group-hover:underline">
                  {post.title}
                </div>
                <div className="flex gap-x-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      <svg
                        className="mr-1.5 h-2 w-2 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 8 8"
                      >
                        <circle cx={4} cy={4} r={3} />
                      </svg>
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="line-clamp-3">{post.description}</div>
              </div>
            </Link>
          ))}
          <div className="flex-1 flex justify-between sm:justify-end">
            <Link
              href={
                current > 1
                  ? {
                      pathname: "/blog/page/[page]",
                      query: { page: (current - 1).toString() },
                    }
                  : "#"
              }
              className={
                "relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md bg-white hover:bg-gray-50 " +
                (current > 1 ? "text-gray-700" : "text-gray-300")
              }
            >
              Previous
            </Link>
            <Link
              href={
                current < totalCount
                  ? {
                      pathname: "/blog/page/[page]",
                      query: { page: (current + 1).toString() },
                    }
                  : "#"
              }
              className={
                "ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md bg-white hover:bg-gray-50 " +
                (current < totalCount ? "text-gray-700" : "text-gray-300")
              }
            >
              Next
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
